import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'
import type { CallOutcome } from './telemetry'

// Types
export interface Contact {
  id: number
  phone: string
  name: string
  email: string
  /** ISO YYYY-MM-DD; null/empty when unknown. */
  birth_date?: string | null
  notes: string
  created_at: number
  updated_at: number
}

interface ContactsResponse {
  contacts: Contact[]
  total: number
  limit: number
  offset: number
}

export interface ContactRecentCall {
  call_sid: string
  direction: string
  status: string
  started_at: number
  duration_s: number | null
  from_number: string
  to_number: string
  ended_by: string | null
  call_summary?: string | null
  /** Outbound: pinned at dial-time from the assistant. Inbound: post-call
   *  classification from the inbound assistant's analysis. */
  purpose?: string | null
  outcome?: CallOutcome | null
  interest_level?: number | null
}

interface ContactDetailResponse {
  contact: Contact
  recent_calls: ContactRecentCall[]
  call_count: number
}

interface ContactPayload {
  phone: string
  name?: string
  email?: string
  notes?: string
}

interface ContactUpdatePayload {
  phone?: string
  name?: string
  email?: string
  birth_date?: string | null
  notes?: string
}

// Hooks
export function useContacts(params?: { search?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams()
  if (params?.search) searchParams.set('search', params.search)
  if (params?.limit) searchParams.set('limit', String(params.limit))
  if (params?.offset) searchParams.set('offset', String(params.offset))
  const qs = searchParams.toString()

  return useQuery({
    queryKey: ['contacts', params],
    queryFn: () => apiFetch<ContactsResponse>(`/contacts${qs ? `?${qs}` : ''}`),
    refetchInterval: 30_000,
  })
}

export function useContact(id: number | undefined) {
  return useQuery({
    queryKey: ['contact', id],
    queryFn: () => apiFetch<ContactDetailResponse>(`/contacts/${id}`),
    enabled: id != null,
  })
}

/** Look up a contact by phone — used by CallStagingPage when the URL only
 *  carries the phone, not a contactId. Returns null silently on 404 so the
 *  briefing can render an empty state instead of an error card. */
export function useContactByPhone(phone: string | undefined) {
  return useQuery({
    queryKey: ['contact-by-phone', phone],
    queryFn: async () => {
      try {
        // Query parameter, not a path segment — see useCallsByPhone.
        const data = await apiFetch<{ contact: Contact }>(
          `/contacts/by-phone?phone=${encodeURIComponent(phone!)}`,
        )
        return data.contact
      } catch (err) {
        // Backend returns 404 when no contact exists for the phone — this is
        // a normal "stage a call to a stranger" path, not an error state.
        if (err instanceof Error && err.message.includes('404')) return null
        throw err
      }
    },
    enabled: !!phone,
  })
}

export function useCreateContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: ContactPayload) =>
      apiFetch<{ contact: Contact }>('/contacts', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useUpdateContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: ContactUpdatePayload & { id: number }) =>
      apiFetch<{ contact: Contact }>(`/contacts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['contact', variables.id] })
    },
  })
}

export function useDeleteContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ success: boolean }>(`/contacts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}
